import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import useGlobalContext from "hooks/globalData";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  AvailableLocationRequest,
  AvailableLicensureBodiesRequest,
  GetCredentialRequest,
  SaveUserCredentialRequest,
  UserScopeRequest,
  AvailableDesignationModel,
  CredentialModel,
  CredentialLicensureBodyModel,
  CredentialLocationModel,
  ProfileCredentialModel,
  UserCredentialAnswerSaveModel,
  UserCredentialSaveRequest,
} from "services/KnowfullyAdminClient";
import CredentialQuestionField from "./CredentialQuestionField";

type CredentialDialogProps = {
  open: boolean;
  userId: string;
  organizationId: string;
  userCredential?: ProfileCredentialModel | null;
  onClose: () => void;
  onSaved: () => void;
};

const CredentialDialog = ({
  open,
  userId,
  organizationId,
  userCredential,
  onClose,
  onSaved,
}: CredentialDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { userProfileCredentialService } = useGlobalContext();
  const isEditMode = !!userCredential;

  const [designations, setDesignations] = useState<AvailableDesignationModel[]>(
    [],
  );
  const [locations, setLocations] = useState<CredentialLocationModel[]>([]);
  const [licensureBodies, setLicensureBodies] = useState<
    CredentialLicensureBodyModel[]
  >([]);
  const [credential, setCredential] = useState<CredentialModel | null>(null);

  const [selectedDesignationId, setSelectedDesignationId] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLicensureBodyId, setSelectedLicensureBodyId] = useState("");
  const [designationOnly, setDesignationOnly] = useState(false);

  const [answers, setAnswers] = useState<
    Record<string, { value: string; answerId?: string }>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDesignations, setLoadingDesignations] = useState(false);

  const scopeRequest = useCallback(
    () =>
      UserScopeRequest.fromJS({
        userId,
        organizationId,
      }),
    [userId, organizationId],
  );

  const resetForm = useCallback(() => {
    setSelectedDesignationId("");
    setSelectedLocationId("");
    setSelectedLicensureBodyId("");
    setDesignationOnly(false);
    setDesignations([]);
    setLocations([]);
    setLicensureBodies([]);
    setCredential(null);
    setAnswers({});
    setError(null);
  }, []);

  const loadCredentialQuestions = useCallback(
    async (
      designationId: string,
      locationId: string,
      licensureBodyId?: string,
    ) => {
      setLoading(true);
      setError(null);
      try {
        const res = await userProfileCredentialService.getCredential(
          GetCredentialRequest.fromJS({
            ...scopeRequest(),
            designationId,
            locationId,
            licensureBodyId,
          }),
        );
        const cred = res.data;
        setCredential(cred ?? null);
        if (cred?.questions) {
          const initial: Record<
            string,
            { value: string; answerId?: string }
          > = {};
          cred.questions.forEach((q) => {
            initial[q.id!] = {
              value: q.userResponse ?? "",
              answerId: q.userResponseId,
            };
          });
          setAnswers(initial);
        }
      } catch {
        setError(t({ id: "fetchError" }));
      } finally {
        setLoading(false);
      }
    },
    [userProfileCredentialService, scopeRequest, t],
  );

  useEffect(() => {
    if (!open) {
      resetForm();
      return;
    }

    if (isEditMode) {
      loadCredentialQuestions(
        userCredential.designation?.id ?? "",
        userCredential.location?.id ?? "",
        userCredential.licensureBody?.id,
      );
    } else {
      setLoadingDesignations(true);
      userProfileCredentialService
        .getAvailableDesignations(scopeRequest())
        .then((res) => setDesignations(res.data ?? []))
        .catch(() => setError(t({ id: "fetchError" })))
        .finally(() => setLoadingDesignations(false));
    }
  }, [
    open,
    isEditMode,
    userCredential,
    userProfileCredentialService,
    resetForm,
    scopeRequest,
    loadCredentialQuestions,
    t,
  ]);

  const handleDesignationOnlyChange = (checked: boolean) => {
    setDesignationOnly(checked);
    if (checked) {
      setSelectedLocationId("");
      setSelectedLicensureBodyId("");
      setLicensureBodies([]);
      setCredential(null);
      setAnswers({});
    }
  };

  const handleDesignationChange = async (designationId: string) => {
    setSelectedDesignationId(designationId);
    setSelectedLocationId("");
    setSelectedLicensureBodyId("");
    setDesignationOnly(false);
    setLocations([]);
    setLicensureBodies([]);
    setCredential(null);
    setAnswers({});
    setError(null);

    if (!designationId) return;

    setLoading(true);
    try {
      const res = await userProfileCredentialService.getAvailableLocations(
        AvailableLocationRequest.fromJS({
          ...scopeRequest(),
          designationId,
        }),
      );
      setLocations(res.data ?? []);
    } catch {
      setError(t({ id: "fetchError" }));
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = async (locationId: string) => {
    setSelectedLocationId(locationId);
    setSelectedLicensureBodyId("");
    setLicensureBodies([]);
    setCredential(null);
    setAnswers({});
    setError(null);

    if (!locationId) return;

    setLoading(true);
    try {
      const res = await userProfileCredentialService.getAvailableLicensureBodies(
        AvailableLicensureBodiesRequest.fromJS({
          ...scopeRequest(),
          designationId: selectedDesignationId,
          locationId,
        }),
      );
      const bodies = res.data ?? [];
      setLicensureBodies(bodies);

      if (bodies.length === 1) {
        setSelectedLicensureBodyId(bodies[0].id!);
        await loadCredentialQuestions(
          selectedDesignationId,
          locationId,
          bodies[0].id!,
        );
      }
    } catch {
      setError(t({ id: "fetchError" }));
    } finally {
      setLoading(false);
    }
  };

  const handleLicensureBodyChange = async (licensureBodyId: string) => {
    setSelectedLicensureBodyId(licensureBodyId);
    setCredential(null);
    setAnswers({});
    setError(null);

    if (!licensureBodyId) return;

    await loadCredentialQuestions(
      selectedDesignationId,
      selectedLocationId,
      licensureBodyId,
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const answersList = Object.entries(answers)
        .filter(([, { value, answerId }]) => value || answerId)
        .map(([questionId, { value, answerId }]) =>
          UserCredentialAnswerSaveModel.fromJS({ questionId, value, answerId }),
        );

      const designationId = isEditMode
        ? userCredential.designation?.id
        : selectedDesignationId;

      let locationId: string | undefined;
      let licensureBodyId: string | undefined;
      if (isEditMode) {
        locationId = userCredential.location?.id;
        licensureBodyId = userCredential.licensureBody?.id;
      } else if (!designationOnly) {
        locationId = selectedLocationId;
        licensureBodyId = selectedLicensureBodyId;
      }

      await userProfileCredentialService.saveUserCredential(
        SaveUserCredentialRequest.fromJS({
          ...scopeRequest(),
          credential: UserCredentialSaveRequest.fromJS({
            designationId,
            locationId,
            licensureBodyId,
            answers: answersList,
          }),
        }),
      );
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedDesignation = designations.find(
    (d) => d.id === selectedDesignationId,
  );

  const canSubmit =
    isEditMode ||
    (selectedDesignationId &&
      (designationOnly || (selectedLocationId && selectedLicensureBodyId)));

  const title = isEditMode
    ? t({ id: "editCredential" })
    : t({ id: "addCredential" });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {isEditMode ? (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    variant="standard"
                    label={t({ id: "designation" })}
                    value={userCredential?.designation?.name ?? ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    variant="standard"
                    label={t({ id: "location" })}
                    value={userCredential?.location?.name ?? ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                {userCredential?.licensureBody && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      variant="standard"
                      label={t({ id: "licensureBody" })}
                      value={userCredential.licensureBody.name ?? ""}
                      fullWidth
                      disabled
                    />
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    variant="standard"
                    label={t({ id: "selectDesignation" })}
                    value={selectedDesignationId}
                    onChange={(e) => handleDesignationChange(e.target.value)}
                    fullWidth
                    select
                    disabled={loadingDesignations}
                  >
                    <MenuItem value="">-</MenuItem>
                    {designations.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    variant="standard"
                    label={t({ id: "selectLocation" })}
                    value={selectedLocationId}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    fullWidth
                    select
                    disabled={
                      designationOnly ||
                      !selectedDesignationId ||
                      !locations.length
                    }
                  >
                    <MenuItem value="">-</MenuItem>
                    {locations.map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    variant="standard"
                    label={t({ id: "selectLicensureBody" })}
                    value={selectedLicensureBodyId}
                    onChange={(e) => handleLicensureBodyChange(e.target.value)}
                    fullWidth
                    select
                    disabled={
                      designationOnly ||
                      !selectedLocationId ||
                      !licensureBodies.length
                    }
                  >
                    <MenuItem value="">-</MenuItem>
                    {licensureBodies.map((lb) => (
                      <MenuItem key={lb.id} value={lb.id}>
                        {lb.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {selectedDesignation &&
                  !selectedDesignation.isDesignationInUse && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <MuiCheckbox
                            checked={designationOnly}
                            onChange={(e) =>
                              handleDesignationOnlyChange(e.target.checked)
                            }
                          />
                        }
                        label={t({ id: "designationOnly" })}
                      />
                    </Grid>
                  )}
              </>
            )}

            {credential?.questions?.map((q) => (
              <Grid item xs={12} md={4} key={q.id}>
                <CredentialQuestionField
                  label={q.value ?? ""}
                  type={q.type!}
                  value={answers[q.id!]?.value ?? ""}
                  required={q.isRequired}
                  answerId={answers[q.id!]?.answerId}
                  choices={q.choices}
                  validationPattern={q.questionValidationRule?.value}
                  validationMessage={q.questionValidationRule?.message}
                  onChange={(value, answerId) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [q.id!]: { value, answerId },
                    }))
                  }
                />
              </Grid>
            ))}
          </Grid>

          {error && (
            <Box mt={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(loading || loadingDesignations) && <CircularProgress size={20} />}
          <Button onClick={onClose} disabled={submitting}>
            {t({ id: "cancelButton" })}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!canSubmit || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            {t({ id: "saveButton" })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CredentialDialog;
